import React, { useState } from 'react';
import { Check, Info, ExternalLink } from 'lucide-react';
import RouterComparisonSheet from './RouterComparisonSheet';

/**
 * RouterOverview component
 * Displays the router image and information about the free-to-use router
 */
const RouterOverview = () => {
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  return (
    <div className="flex flex-col md:flex-row items-center mb-12 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-2xl p-6 border border-purple-800/30">
      <div className="md:w-1/3 flex justify-center mb-6 md:mb-0">
        <div 
          className="relative w-64 h-64 cursor-pointer group"
          onClick={() => setIsComparisonOpen(true)}
        >
          <img
            src="https://apextech.group/elements-mesh-router-1.png"
            alt="Elements Mesh Router"
            className="w-full h-full object-contain transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-purple-900/80 text-white px-3 py-2 rounded-lg flex items-center text-sm font-medium">
              <ExternalLink className="w-4 h-4 mr-1" /> View Specifications
            </div>
          </div>
        </div>
      </div>
      <div className="md:w-2/3 md:pl-8">
        <h3 className="text-2xl font-bold text-white mb-2">Free-to-Use Routers</h3>
        <p className="text-gray-300 mb-4">
          We provide a high-performance router <span className="text-green-400 font-semibold">free-to-use</span>* with your internet package. The model you receive depends on your plan speed:
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div 
            className="bg-gray-800/70 rounded-lg p-3 cursor-pointer hover:bg-gray-700/70 transition-colors group"
            onClick={() => setIsComparisonOpen(true)}
          >
            <h4 className="text-purple-300 font-medium text-sm">100Mbps and Below</h4>
            <p className="text-white text-base">EX141 AX1500 Router</p>
            <p className="text-xs text-purple-300 mt-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
              <ExternalLink className="w-3 h-3 mr-1" /> View specifications
            </p>
          </div>
          <div 
            className="bg-gray-800/70 rounded-lg p-3 cursor-pointer hover:bg-gray-700/70 transition-colors group"
            onClick={() => setIsComparisonOpen(true)}
          >
            <h4 className="text-purple-300 font-medium text-sm">Above 100Mbps</h4>
            <p className="text-white text-base">Elements Mesh Router</p>
            <p className="text-xs text-purple-300 mt-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
              <ExternalLink className="w-3 h-3 mr-1" /> View specifications
            </p>
          </div>
        </div>
        
        {/* Main Feature Highlight */}
        <div className="bg-green-900/30 border border-green-700/30 rounded-lg p-4 mb-6">
          <h4 className="text-green-400 font-semibold text-lg mb-2 flex items-center">
            <Check className="h-5 w-5 mr-2" /> * Free to Use
          </h4>
          <p className="text-gray-300">
            We provide this high-quality router at no additional cost with your internet package.
            There's no rental fee or hidden charges - it's completely free to use for as long as
            you're our customer.
          </p>
        </div>
        
        {/* Router Features */}
        <div className="bg-purple-900/30 border border-purple-700/30 rounded-lg p-4">
          <h4 className="text-purple-400 font-semibold text-lg mb-2">Key Features</h4>
          <ul className="text-gray-300 space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-purple-400 mr-2 mt-0.5" />
              <span>Dual-band WiFi 6 technology for faster speeds</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-purple-400 mr-2 mt-0.5" />
              <span>Wi-Fi CERTIFIED EasyMesh™ for expandable coverage</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-purple-400 mr-2 mt-0.5" />
              <span>Advanced security with WPA3 encryption</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-purple-400 mr-2 mt-0.5" />
              <span>Smart app control for easy setup and management</span>
            </li>
          </ul>
        </div>
        
        {/* Small footnote */}
        <p className="text-gray-500 text-xs mt-4 italic">
          * The router remains our property and must be returned in good working condition if you cancel your service.
        </p>
      </div>
      
      {/* Router Comparison Sheet */}
      <RouterComparisonSheet 
        isOpen={isComparisonOpen} 
        onClose={() => setIsComparisonOpen(false)} 
      />
    </div>
  );
};

export default RouterOverview;